import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveSalonMediaUrl } from '../salon/utils/mediaUrl';
import ReportPdfQrInline from '../shared/ReportPdfQrInline';
import {
  createLobbyHairReportPdfUrl,
  createLobbySkinReportPdfUrl,
} from '../salon/utils/lobbyReportPdfApi';
import './RecommendedProductsPreviewModal.css';

function flattenProducts(recommended) {
  const morning = Array.isArray(recommended?.morning) ? recommended.morning : [];
  const evening = Array.isArray(recommended?.evening) ? recommended.evening : [];
  const seen = new Set();
  const out = [];
  for (const p of [...morning, ...evening]) {
    if (!p || typeof p !== 'object') continue;
    const key = String(p.id || p.product_id || p.product_name || p.name || Math.random());
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

function productKey(product) {
  return String(product?.id || product?.product_id || product?.product_name || product?.name || '');
}

function cartToQtyMap(cart) {
  const map = {};
  for (const item of Array.isArray(cart) ? cart : []) {
    const product = item?.product && typeof item.product === 'object' ? item.product : item;
    const key = productKey(product);
    if (!key) continue;
    const qty = Number(item?.quantity || product?.quantity || 0);
    if (qty > 0) map[key] = qty;
  }
  return map;
}

function cartFingerprint(cart) {
  const map = cartToQtyMap(cart);
  return Object.keys(map)
    .sort()
    .map((k) => `${k}:${map[k]}`)
    .join('|');
}

function normalizeTreatments(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const t of list) {
    if (!t || typeof t !== 'object') continue;
    const key = String(t.id || t.treatment_id || t.treatment || t.name || Math.random());
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function buildTreatmentDetailSections(item) {
  const sections = [];
  const push = (label, value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      const list = value.map((v) => String(v || '').trim()).filter(Boolean);
      if (!list.length) return;
      sections.push({ label, value: list.join(', '), list });
      return;
    }
    const text = String(value).trim();
    if (!text) return;
    sections.push({ label, value: text });
  };
  push('About', item?.about_treatment || item?.description);
  push('Key benefit', item?.major_usp);
  push('Kit / Product', item?.kit);
  push('Core ingredients', item?.core_ingredients || item?.ingredient);
  push('Suggested post care', item?.post_care);
  push('Rate card', item?.rate_card);
  push('Brand', item?.brand);
  push('Addresses', item?.conditions);
  push('Goals', item?.skin_goals);
  const priceBits = [];
  if (item?.net_price != null && item?.net_price !== '') priceBits.push(`₹${item.net_price}`);
  if (
    item?.sale_price != null &&
    item?.sale_price !== '' &&
    String(item.sale_price) !== String(item.net_price)
  ) {
    priceBits.push(`sale ₹${item.sale_price}`);
  }
  if (item?.price && !priceBits.length) priceBits.push(String(item.price));
  if (item?.amount != null && item?.amount !== '' && !priceBits.length) {
    priceBits.push(`₹${item.amount}`);
  }
  if (priceBits.length) push('Price', priceBits.join(' · '));
  return sections;
}

/**
 * Desk + Passport shared product/treatment upsell preview with cart / place order.
 */
export default function RecommendedProductsPreviewModal({
  open,
  onClose,
  customerName = 'Guest',
  products = null,
  treatments = null,
  loading = false,
  error = '',
  customerFacing = false,
  allowCart = true,
  allowPlaceOrder = true,
  initialCart = null,
  purchases = null,
  subtitle = '',
  busy = false,
  notice = '',
  onSaveCart,
  onPlaceOrder,
  skinSessionId = '',
  hairSessionId = '',
}) {
  const skinId = String(skinSessionId || '').trim();
  const hairId = String(hairSessionId || '').trim().startsWith('HA-')
    ? String(hairSessionId || '').trim()
    : '';

  const createSkinPdfUrl = useCallback(
    () => createLobbySkinReportPdfUrl(skinId, customerName),
    [skinId, customerName],
  );
  const createHairPdfUrl = useCallback(
    () => createLobbyHairReportPdfUrl(hairId, customerName),
    [hairId, customerName],
  );
  const items = useMemo(() => flattenProducts(products), [products]);
  const treatmentItems = useMemo(() => normalizeTreatments(treatments), [treatments]);
  const [qtyById, setQtyById] = useState({});
  const [dirty, setDirty] = useState(false);
  const [localError, setLocalError] = useState('');
  const [localNotice, setLocalNotice] = useState('');
  const [clearing, setClearing] = useState(false);
  const [detailTreatment, setDetailTreatment] = useState(null);
  const dirtyRef = useRef(false);
  const wasOpenRef = useRef(false);
  const lastServerCartFpRef = useRef('');
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  // Seed from server only on open, or when server cart changes and user is not editing.
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      dirtyRef.current = false;
      setDirty(false);
      lastServerCartFpRef.current = '';
      setDetailTreatment(null);
      return;
    }
    const fp = cartFingerprint(initialCart);
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (justOpened) {
      setQtyById(cartToQtyMap(initialCart));
      dirtyRef.current = false;
      setDirty(false);
      lastServerCartFpRef.current = fp;
      setLocalError('');
      setLocalNotice('');
      return;
    }
    if (dirtyRef.current) return;
    if (fp !== lastServerCartFpRef.current) {
      lastServerCartFpRef.current = fp;
      setQtyById(cartToQtyMap(initialCart));
    }
  }, [open, initialCart]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !customerFacing) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, customerFacing]);

  const cartItems = useMemo(() => {
    const out = [];
    for (const product of items) {
      const key = productKey(product);
      const qty = Number(qtyById[key] || 0);
      if (qty > 0) out.push({ product, quantity: qty });
    }
    return out;
  }, [items, qtyById]);

  const cartCount = cartItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
  const purchaseCount = Array.isArray(purchases) ? purchases.length : 0;
  const canPersistCart = dirty || cartCount > 0;
  const isClearAction = dirty && cartCount === 0;

  const bumpQty = (product, delta) => {
    const key = productKey(product);
    if (!key) return;
    dirtyRef.current = true;
    setDirty(true);
    setQtyById((prev) => {
      const next = { ...prev };
      const val = Math.max(0, Number(next[key] || 0) + delta);
      if (val <= 0) delete next[key];
      else next[key] = val;
      return next;
    });
  };

  const persistCart = async (itemsToSave, { cleared = false } = {}) => {
    if (!onSaveCart || saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setLocalError('');
    setLocalNotice('');
    if (cleared) setClearing(true);
    try {
      await onSaveCart(itemsToSave);
      const fp = cartFingerprint(itemsToSave);
      lastServerCartFpRef.current = fp;
      dirtyRef.current = false;
      setDirty(false);
      setQtyById(cartToQtyMap(itemsToSave));
      setLocalNotice(
        cleared
          ? 'Cart cleared.'
          : customerFacing
            ? 'Added for front desk — they can complete your purchase.'
            : 'Cart saved. Front desk can confirm purchase.',
      );
    } catch (err) {
      setLocalError(err?.message || 'Could not save cart');
    } finally {
      saveInFlightRef.current = false;
      setClearing(false);
    }
  };

  const handleSaveCart = async () => {
    if (!canPersistCart) return;
    await persistCart(cartItems, { cleared: isClearAction });
  };

  // Persist empty cart when user steps qty down to 0 (clear must reach server).
  useEffect(() => {
    if (!open || !allowCart || !onSaveCart || !dirty || cartCount > 0) return undefined;
    const timer = setTimeout(() => {
      void persistCart([], { cleared: true });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when cart emptied while dirty
  }, [open, allowCart, onSaveCart, dirty, cartCount]);

  const handlePlace = async () => {
    if (!onPlaceOrder) return;
    if (!cartCount) {
      setLocalError('Add at least one product to the cart first');
      return;
    }
    setLocalError('');
    setLocalNotice('');
    try {
      await onPlaceOrder(cartItems);
      setDirty(false);
      lastServerCartFpRef.current = '';
      setLocalNotice('Purchase recorded for this visit (My Orders / analytics).');
      setQtyById({});
    } catch (err) {
      setLocalError(err?.message || 'Could not place order');
    }
  };

  if (!open) return null;

  const saveLabel = isClearAction
    ? 'Clear cart'
    : customerFacing
      ? 'Send to front desk'
      : 'Save cart';

  const hasAnyReco = items.length > 0 || treatmentItems.length > 0;
  const detailSections = detailTreatment ? buildTreatmentDetailSections(detailTreatment) : [];
  const detailTitle =
    detailTreatment?.treatment || detailTreatment?.name || 'Treatment details';
  const detailImage = resolveSalonMediaUrl(
    detailTreatment?.image_url || detailTreatment?.image || '',
  );

  return (
    <div
      className={`reco-preview-modal${customerFacing ? ' reco-preview-modal--customer' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Recommended products and services"
      onClick={(e) => {
        if (e.target === e.currentTarget && !customerFacing) onClose?.();
      }}
    >
      <div className="reco-preview-modal__panel">
        <header className="reco-preview-modal__header">
          <div>
            <p className="reco-preview-modal__eyebrow">
              {customerFacing ? 'Recommended for you' : 'Recommendations'}
            </p>
            <h2 className="reco-preview-modal__title">{customerName}</h2>
            {subtitle ? <p className="reco-preview-modal__sub">{subtitle}</p> : null}
            {purchaseCount ? (
              <p className="reco-preview-modal__sub">
                Purchased this visit · {purchaseCount} product{purchaseCount === 1 ? '' : 's'}
              </p>
            ) : null}
          </div>
          {!customerFacing ? (
            <button
              type="button"
              className="reco-preview-modal__close"
              onClick={onClose}
              disabled={busy || clearing}
            >
              Close
            </button>
          ) : null}
        </header>

        <div className="reco-preview-modal__body">
          {(skinId || hairId) ? (
            <section className="reco-preview-modal__reports" aria-label="Analysis report downloads">
              <p className="reco-preview-modal__reports-lede">
                {skinId && hairId
                  ? 'Skin and hair PDFs for this guest — scan each QR or open on this device.'
                  : 'Scan to download the PDF on a phone.'}
              </p>
              {skinId ? (
                <ReportPdfQrInline
                  key={`skin-${skinId}`}
                  enabled={Boolean(skinId)}
                  createDownloadUrl={createSkinPdfUrl}
                  title="Skin analysis report"
                  subtitle={skinId && hairId ? '' : 'Scan to save the PDF on a phone.'}
                />
              ) : null}
              {hairId ? (
                <ReportPdfQrInline
                  key={`hair-${hairId}`}
                  enabled={Boolean(hairId)}
                  createDownloadUrl={createHairPdfUrl}
                  title="Hair analysis report"
                  subtitle={skinId && hairId ? '' : 'Scan to save the PDF on a phone.'}
                />
              ) : null}
            </section>
          ) : null}
          {loading ? <p className="reco-preview-modal__status">Loading recommendations…</p> : null}
          {!loading && (error || localError) ? (
            <p className="reco-preview-modal__error">{error || localError}</p>
          ) : null}
          {!loading && (notice || localNotice) ? (
            <p className="reco-preview-modal__notice">{notice || localNotice}</p>
          ) : null}
          {!loading && !error && !hasAnyReco ? (
            <p className="reco-preview-modal__status">
              No recommended products or services for this visit.
            </p>
          ) : null}

          {!loading && treatmentItems.length ? (
            <section className="reco-preview-modal__section" aria-label="Recommended services">
              <h3 className="reco-preview-modal__section-title">Recommended services</h3>
              <ul className="reco-preview-modal__grid">
                {treatmentItems.map((treatment, idx) => {
                  const title =
                    treatment.treatment || treatment.name || `Service ${idx + 1}`;
                  const key = String(treatment.id || title);
                  const imageUrl = resolveSalonMediaUrl(treatment.image_url || treatment.image);
                  const price =
                    treatment.sale_price ||
                    treatment.net_price ||
                    treatment.amount ||
                    treatment.price;
                  const brand = Array.isArray(treatment.brand)
                    ? treatment.brand.filter(Boolean).join(', ')
                    : treatment.brand || '';
                  const desc =
                    treatment.suggested_for_wording ||
                    treatment.major_usp ||
                    treatment.about_treatment ||
                    treatment.description ||
                    '';
                  return (
                    <li key={`t-${key}-${idx}`} className="reco-preview-modal__card">
                      <div className="reco-preview-modal__media">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.style.visibility = 'hidden';
                            }}
                          />
                        ) : (
                          <div className="reco-preview-modal__media-fallback" aria-hidden="true" />
                        )}
                      </div>
                      <div className="reco-preview-modal__meta">
                        {brand ? <p className="reco-preview-modal__brand">{brand}</p> : null}
                        <h3>{title}</h3>
                        {price != null && String(price).trim() !== '' ? (
                          <p className="reco-preview-modal__price">
                            {String(price).includes('₹') ? price : `₹${price}`}
                          </p>
                        ) : null}
                        {desc ? <p className="reco-preview-modal__desc">{desc}</p> : null}
                        <button
                          type="button"
                          className="reco-preview-modal__details"
                          onClick={() => setDetailTreatment(treatment)}
                        >
                          View details
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {!loading && items.length ? (
            <section className="reco-preview-modal__section" aria-label="Recommended products">
              <h3 className="reco-preview-modal__section-title">Recommended products</h3>
              <ul className="reco-preview-modal__grid">
                {items.map((product, idx) => {
                  const title = product.product_name || product.name || `Product ${idx + 1}`;
                  const key = productKey(product) || `${title}-${idx}`;
                  const imageUrl = resolveSalonMediaUrl(product.image_url || product.image);
                  const price = product.price || product.amount || product.sale_price;
                  const brand = product.brand || '';
                  const qty = Number(qtyById[key] || 0);
                  return (
                    <li key={`${key}-${idx}`} className="reco-preview-modal__card">
                      <div className="reco-preview-modal__media">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.style.visibility = 'hidden';
                            }}
                          />
                        ) : (
                          <div className="reco-preview-modal__media-fallback" aria-hidden="true" />
                        )}
                      </div>
                      <div className="reco-preview-modal__meta">
                        {brand ? <p className="reco-preview-modal__brand">{brand}</p> : null}
                        <h3>{title}</h3>
                        {price != null && String(price).trim() !== '' ? (
                          <p className="reco-preview-modal__price">₹{price}</p>
                        ) : null}
                        {product.short_description ||
                        product.description ||
                        product.suggested_for_wording ? (
                          <p className="reco-preview-modal__desc">
                            {product.suggested_for_wording ||
                              product.short_description ||
                              product.description}
                          </p>
                        ) : null}
                        {allowCart ? (
                          <div className="reco-preview-modal__qty">
                            <button
                              type="button"
                              className="reco-preview-modal__qty-btn"
                              disabled={busy || clearing || qty <= 0}
                              onClick={() => bumpQty(product, -1)}
                              aria-label="Decrease quantity"
                            >
                              −
                            </button>
                            <span className="reco-preview-modal__qty-val">{qty}</span>
                            <button
                              type="button"
                              className="reco-preview-modal__qty-btn"
                              disabled={busy || clearing}
                              onClick={() => bumpQty(product, 1)}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                            {qty === 0 ? (
                              <button
                                type="button"
                                className="reco-preview-modal__add"
                                disabled={busy || clearing}
                                onClick={() => bumpQty(product, 1)}
                              >
                                Add
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
        </div>

        <footer className="reco-preview-modal__footer">
          <p>
            {customerFacing
              ? 'Add products for the front desk to complete your purchase.'
              : 'Cart syncs to Passport. Confirm purchase to store in My Orders / analytics.'}
            {cartCount ? ` · Cart ${cartCount}` : dirty ? ' · Cart empty' : ''}
          </p>
          <div className="reco-preview-modal__actions">
            {allowCart && onSaveCart ? (
              <button
                type="button"
                className="reco-preview-modal__secondary"
                disabled={busy || clearing || !canPersistCart}
                onClick={handleSaveCart}
              >
                {clearing ? 'Clearing…' : saveLabel}
              </button>
            ) : null}
            {allowPlaceOrder && onPlaceOrder && !customerFacing ? (
              <button
                type="button"
                className="reco-preview-modal__done"
                disabled={busy || clearing || !cartCount}
                onClick={handlePlace}
              >
                Confirm purchase
              </button>
            ) : null}
            {!customerFacing ? (
              <button
                type="button"
                className="reco-preview-modal__secondary"
                disabled={busy || clearing}
                onClick={onClose}
              >
                Done
              </button>
            ) : null}
          </div>
        </footer>
      </div>

      {detailTreatment ? (
        <div
          className="reco-preview-modal__detail"
          role="dialog"
          aria-modal="true"
          aria-label={detailTitle}
          onClick={(e) => {
            if (e.target === e.currentTarget) setDetailTreatment(null);
          }}
        >
          <div className="reco-preview-modal__detail-panel">
            <header className="reco-preview-modal__detail-header">
              <h3>{detailTitle}</h3>
              <button
                type="button"
                className="reco-preview-modal__close"
                onClick={() => setDetailTreatment(null)}
              >
                Close
              </button>
            </header>
            <div className="reco-preview-modal__detail-body">
              {detailImage ? (
                <div className="reco-preview-modal__detail-media">
                  <img
                    src={detailImage}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              ) : null}
              {detailSections.length ? (
                detailSections.map((section) => (
                  <div key={section.label} className="reco-preview-modal__detail-section">
                    <h4>{section.label}</h4>
                    {Array.isArray(section.list) ? (
                      <div className="reco-preview-modal__detail-tags">
                        {section.list.map((tag) => (
                          <span key={`${section.label}-${tag}`}>{tag}</span>
                        ))}
                      </div>
                    ) : (
                      <p>{section.value}</p>
                    )}
                  </div>
                ))
              ) : (
                <p>No additional details available.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
